import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { SectionCard } from '@/components/ui';
import type { RevenueChart, DashboardSummary } from '../types/dashboardWidget';

type Metric = 'revenue' | 'orders';

interface RevenueHeroPanelProps {
  summary?: DashboardSummary | undefined;
  chart?: RevenueChart | undefined;
  isLoading?: boolean | undefined;
  title: string;
  labels: {
    revenueTab: string;
    ordersTab: string;
    totalRevenue: string;
    totalOrders: string;
    avgDailyRevenue: string;
    avgDailyOrders: string;
    revenueTooltip: string;
    ordersTooltip: string;
    comparedToPrevDay: string;
  };
}

const formatKRWFull = (value: number) =>
  new Intl.NumberFormat('ko-KR').format(value) + '원';

const formatKRWCompact = (value: number) =>
  new Intl.NumberFormat('ko-KR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

const formatCount = (value: number) =>
  new Intl.NumberFormat('ko-KR').format(value) + '건';

const formatDelta = (delta: number): string => {
  const abs = Math.abs(delta);
  const formatted = abs.toFixed(abs >= 10 ? 0 : 1);
  return `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${formatted}%`;
};

export const RevenueHeroPanel = ({
  summary,
  chart,
  isLoading,
  title,
  labels,
}: RevenueHeroPanelProps) => {
  const [metric, setMetric] = useState<Metric>('revenue');
  const daily = chart?.daily ?? [];

  const totalRevenue = summary?.totalRevenue ?? chart?.totalRevenue ?? 0;
  const totalOrders = summary?.totalOrders ?? 0;
  const avgDailyRevenue = chart?.avgDailyRevenue ?? summary?.totalRevenue
    ? Math.round((summary?.totalRevenue ?? 0) / Math.max(daily.length, 1))
    : 0;
  const avgDailyOrders = summary?.avgDailyOrders ?? 0;
  const revenueDelta = summary?.revenueChangeRate ?? null;

  const headlineValue =
    metric === 'revenue' ? formatKRWFull(totalRevenue) : formatCount(totalOrders);
  const subValue =
    metric === 'revenue'
      ? `${labels.avgDailyRevenue} ${formatKRWFull(avgDailyRevenue)}`
      : `${labels.avgDailyOrders} ${formatKRWCompact(avgDailyOrders)}건`;

  const direction = revenueDelta == null ? 'flat' : revenueDelta > 0 ? 'up' : revenueDelta < 0 ? 'down' : 'flat';
  const DeltaIcon = direction === 'up' ? ArrowUp : direction === 'down' ? ArrowDown : Minus;
  const deltaColor =
    direction === 'up'
      ? 'var(--status-success-fg)'
      : direction === 'down'
        ? 'var(--status-danger-fg)'
        : 'var(--content-muted)';
  const deltaBg =
    direction === 'up'
      ? 'var(--status-success-bg)'
      : direction === 'down'
        ? 'var(--status-danger-bg)'
        : 'var(--surface-1)';

  return (
    <SectionCard
      title={title}
      aside={
        <div
          role="tablist"
          className="inline-flex items-center gap-1 rounded-[var(--radius-button)] p-1"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
        >
          {(['revenue', 'orders'] as const).map((m) => {
            const selected = metric === m;
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setMetric(m)}
                className="rounded-[var(--radius-button)] px-3 py-1 text-[12px] font-medium transition-all"
                style={{
                  background: selected ? 'var(--surface-raised)' : 'transparent',
                  color: selected ? 'var(--content-strong)' : 'var(--content-muted)',
                  boxShadow: selected ? 'var(--shadow-card-rest)' : 'none',
                }}
                data-testid={`revenue-hero-tab-${m}`}
              >
                {m === 'revenue' ? labels.revenueTab : labels.ordersTab}
              </button>
            );
          })}
        </div>
      }
      data-testid="revenue-hero-panel"
    >
      <div className="flex flex-col gap-6">
        {/* Big number + delta + sub */}
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="flex flex-col gap-1">
            <span
              className="text-[12px] font-medium uppercase tracking-[0.08em]"
              style={{ color: 'var(--content-muted)' }}
            >
              {metric === 'revenue' ? labels.totalRevenue : labels.totalOrders}
            </span>
            <strong
              className="text-[36px] font-semibold leading-none tracking-tight tabular-nums"
              style={{ color: 'var(--content-strong)' }}
              data-testid="revenue-hero-value"
            >
              {isLoading ? '—' : headlineValue}
            </strong>
          </div>
          {revenueDelta != null && metric === 'revenue' ? (
            <div className="flex items-center gap-2 pb-1">
              <span
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold tabular-nums"
                style={{ background: deltaBg, color: deltaColor }}
              >
                <DeltaIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                {formatDelta(revenueDelta)}
              </span>
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {labels.comparedToPrevDay}
              </span>
            </div>
          ) : null}
          <div className="ml-auto pb-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {isLoading ? '—' : subValue}
          </div>
        </div>

        {/* Chart */}
        <div className="h-[200px]" data-testid="revenue-hero-chart">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
                —
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {metric === 'revenue' ? (
                <AreaChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="revHeroFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--cta-primary)" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="var(--cta-primary)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--divider-strong)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'var(--content-muted)' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={formatKRWCompact}
                    tick={{ fontSize: 11, fill: 'var(--content-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    width={48}
                  />
                  <Tooltip
                    cursor={{ stroke: 'var(--cta-primary)', strokeWidth: 1, strokeDasharray: '3 3' }}
                    contentStyle={{
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--divider-strong)',
                      borderRadius: 'var(--radius-card)',
                      fontSize: 12,
                      boxShadow: 'var(--elevation-2)',
                    }}
                    formatter={(value) => [formatKRWFull(Number(value)), labels.revenueTooltip]}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="var(--cta-primary)"
                    strokeWidth={2.5}
                    fill="url(#revHeroFill)"
                    animationDuration={800}
                  />
                </AreaChart>
              ) : (
                <LineChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--divider-strong)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'var(--content-muted)' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--content-muted)' }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                  />
                  <Tooltip
                    cursor={{ stroke: 'var(--status-info-fg)', strokeWidth: 1, strokeDasharray: '3 3' }}
                    contentStyle={{
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--divider-strong)',
                      borderRadius: 'var(--radius-card)',
                      fontSize: 12,
                      boxShadow: 'var(--elevation-2)',
                    }}
                    formatter={(value) => [`${Number(value)}건`, labels.ordersTooltip]}
                  />
                  <Line
                    type="monotone"
                    dataKey="orderCount"
                    stroke="var(--status-info-fg)"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                    animationDuration={800}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </SectionCard>
  );
};
